/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2022 ICEMEDX, Inc.
***********************************************/
import { IceMultimediaCanvas } from './IceMultimediaCanvas';
import { IceMultimediaText } from './IceMultimediaText';
import { IcegraffitiData } from './IceMultimediaData';
import { IceMultimediaStroke, IcePoint, IceRect } from './IceMultimediaStroke';
import { IceMultimediaAttributes } from './IceMultimediaAttributes';
import { Iceutility } from '../../services/iceutility';

export enum multimediaModes {
  modeErase = 1,
  modeDraw = 2,
  modeText = 3,
  modeRecorder = 4
}

export enum canvasRenderingComposition {
  destinationOut = 'destination-out',
  sourceOver = 'source-over'
}

export enum strokeColors {
  black = 'black',
  white = 'white'
}

export class IceMultimediaPage {
  defaultMode = multimediaModes.modeDraw;

  strokeList: IceMultimediaStroke[] = [];
  textList: IceMultimediaText[] = [];
  dataList: IcegraffitiData[] = [];
  pageData: string = "";
  pageId: string;

  modified: boolean = false;

  constructor() {
    this.pageId = Iceutility.guid(true);
  }

  newStroke(attr: IceMultimediaAttributes, pt: IcePoint): IceMultimediaStroke {
    let strokeItem: IceMultimediaStroke  = null;
    try {
      strokeItem = new IceMultimediaStroke(attr, pt);
      this.strokeList.push(strokeItem);
      this.modified = true; // Mark the document as having been modified, for
    }
    catch (e) {
        console.error(e.message);
    }
    return strokeItem;
  }

  newText(attr: IceMultimediaAttributes, x: number, y: number, dat: string): IceMultimediaText {
    let textItem: IceMultimediaText = null;
    try {
      textItem = new IceMultimediaText(attr, new IcePoint(x, y), dat);
      this.textList.push(textItem);
      this.modified = true; // Mark the document as having been modified, for
    }
    catch (e) {
      console.error(e.message);
    }
    return textItem;
  }

  deleteStroke(canvas: IceMultimediaCanvas, x: number, y: number) {
    try {
      var linewidth = canvas.context.lineWidth;
      canvas.context.moveTo(x, y);
      canvas.context.strokeStyle = strokeColors.white;
      canvas.context.lineWidth = linewidth + 2;
      canvas.context.globalCompositeOperation = canvasRenderingComposition.destinationOut;
      canvas.context.arc(x, y, 4, 0 * Math.PI, 1 * Math.PI);
      canvas.context.fill();

      for (let iStroke = 0; iStroke < canvas.page.strokeList.length; iStroke++) {
        let stroke = canvas.page.strokeList[iStroke];

        for (let iPoint = 0; iPoint < stroke.pointArray.length; iPoint++) {
          let point = stroke.pointArray[iPoint];

          if ((point.x == x || (point.x - x) >= 1.1) && (point.y === y || (point.y - y) >= 1.1)) {
            if (point.x === -1 && point.y === -1) {
              continue;
            }

            point.x = -1;
            point.y = -1;
            break;
          }
        }
      }

      // Delete texts
      this.deleteText(canvas, x, y);

      // Restore original context settings
      canvas.context.lineWidth = linewidth;
      canvas.context.strokeStyle = canvas.defaultAttributes.fillcolor;
      canvas.context.globalCompositeOperation = canvasRenderingComposition.sourceOver;
    } catch (e) {
      console.log(e.message);
    }
  }

  deleteText(canvas: IceMultimediaCanvas, x, y) {
    try {
      canvas.context.beginPath();

      for (var iText = 0; iText < this.textList.length; iText++) {
        var temp = this.textList[iText];

        if (x >= temp.point.x && (x <= (temp.point.x + temp.attributes.width) || x - (temp.point.x + temp.attributes.width) <= 10) && y <= temp.point.y && (y >= (temp.point.y - 10) || x - (temp.point.y - 10) <= 15)) {
          this.textList.splice(iText, 1);
          canvas.context.clearRect(temp.point.x, temp.point.y - 10, temp.attributes.width + 10, 10);
          x = temp.point.x; y = temp.point.y;
          break;
        }
      }
    } catch (e) {
      console.log(e.message);
    }

    return { x: x, y: y };
  }

  draw(canvas: IceMultimediaCanvas, x, y, text: string = ""): IcePoint {
    if (canvas == null) return;

    let pt = new IcePoint(x, y);
    // Draw the strokes, text and data
    switch (this.defaultMode) {
      case multimediaModes.modeText:
        let textItem = new IceMultimediaText(canvas.defaultAttributes, pt, text);
        textItem.draw(canvas);
        break;
      case multimediaModes.modeDraw:
      default:
        let stroke = new IceMultimediaStroke(canvas.defaultAttributes, pt);
        stroke.draw(canvas);
        break;
    }

    return pt;
  }
};
